import type { TFunction } from "i18next";
import type { TrendPoint } from "../types";

/**
 * Boshqaruv panelining sof hisob funksiyalari.
 *
 * ⚠️ **`packages/domain` GA YOZILMAYDI.** (a) `packages/domain` ning
 * barrel fayli (`src/index.ts`) bu blokning egaligida emas, ya'ni unga
 * yozish egasiz umumiy fayl o'zgarishi bo'lardi; (b) backend `foiz` ni
 * serverda hisoblamaydi (`AttendanceTrendSchema` izohi: «Server FOIZ
 * QAYTARMAYDI»), ya'ni ikkinchi iste'molchi YO'Q — sof funksiyani
 * `domain` ga chiqarishning yagona asosi bo'lishish edi.
 */

/**
 * Davomat darajasi (foiz).
 *
 * ⚠️⚠️ SURAT — `boshlangan` (`first_check_in_at IS NOT NULL`), `kelgan`
 * EMAS. `hisoblaStatus` ustuvorligida `davom_etmoqda` `kechikkan` ni,
 * `kechikkan` esa `erta_ketgan` ni SOYALAYDI, ya'ni `status = 'kelgan'`
 * faqat «kelib, ketib, kechikmagan va erta ketmagan» kun.
 * `kelgan/plannedShifts` butun ish kuni davomida haqiqiy darajani past
 * ko'rsatib, kechqurun o'z-o'zidan «tuzalardi» — hech qanday test qizil
 * bermasdi.
 *
 * ⚠️ **`plannedShifts <= 0` da `null`, `0` EMAS.** Dam olish kunida yoki
 * smenasi yo'q bo'limda `0/0 = NaN` UI da «NaN%» berardi va u «0%» dan
 * farq qilmasdi; `0%` esa bo'limni ro'yxatning eng pastida «eng yomon
 * bo'lim» qilib turardi.
 *
 * ⚠️ **Natija 100% da CHEGARALANADI.** `boshlangan > plannedShifts`
 * jadvalsiz kunda check-in bo'lganda yuz beradi (smena keyinroq
 * o'zgartirilgan yoki backfill) — cheklovsiz «142,0%» kabi qiymat UI da
 * chalkash ko'rinardi.
 */
export function davomatDarajasi(boshlangan: number, plannedShifts: number): number | null {
	if (plannedShifts <= 0) {
		return null;
	}

	return Math.min(100, (boshlangan / plannedShifts) * 100);
}

/**
 * Foizning ko'rinadigan matni.
 *
 * ⚠️ Kasr belgisi — VERGUL (o'zbekcha), nuqta emas. `null` esa «—»:
 * «0,0%» ma'lumot yo'qligini «hech kim kelmadi» deb ko'rsatardi.
 */
export function foizMatn(foiz: number | null, kasr = 1): string {
	return foiz === null ? "—" : `${foiz.toFixed(kasr).replace(".", ",")}%`;
}

/**
 * Trend seriyasining BUGUNGI nuqtasini jonli sanoqlar bilan almashtiradi.
 *
 * ⚠️⚠️ Hero karta `/dashboard` dan (20 s poll), sparkline
 * `/dashboard/trend` dan (10 daqiqa `staleTime`) keladi. Ular
 * sinxronlanmasa BITTA savolga IKKI javob berardi: hero 92,0%,
 * sparkline'ning oxirgi nuqtasi esa 10 daqiqa eski qiymat. Almashtirish
 * CHIZISHDAN OLDIN bajariladi.
 *
 * ⚠️ Yangi nuqta QO'SHILMAYDI — faqat mavjudi almashtiriladi. Sanoq
 * sanasi seriyada bo'lmasa (masalan «Kecha» rejimida `to` boshqa) massiv
 * tegilmaydi.
 */
export function mergeBugun(points: TrendPoint[], sanoq: TrendPoint | undefined): TrendPoint[] {
	if (!sanoq) {
		return points;
	}

	return points.map((nuqta) => (nuqta.date === sanoq.date ? sanoq : nuqta));
}

/** Juft uzunlikda o'rtadagi ikkitasining o'rtachasi. */
function mediana(qiymatlar: number[]): number {
	const tartib = [...qiymatlar].sort((a, b) => a - b);
	const orta = Math.floor(tartib.length / 2);

	if (tartib.length % 2 === 1) {
		return tartib[orta] ?? 0;
	}

	return ((tartib[orta - 1] ?? 0) + (tartib[orta] ?? 0)) / 2;
}

/**
 * Bugungi daraja va oldingi kunlar medianasi orasidagi farq (p.p.).
 *
 * ⚠️⚠️ **Baza — 30 kunlik MEDIANA, «kecha» EMAS.** Bu ONGLI mahsulot
 * qarori: kanvas «kechagi kunga nisbatan» deydi, lekin kechagi kun
 * dushanbada va bayramdan keyin sakraydi va signal bermaydi; bundan
 * tashqari `plannedShifts = 0` bo'lgan dam kunida «kecha» bazasi UMUMAN
 * yo'q bo'lardi. Yorliq matni ham hisobga MOS yoziladi («30 kunlik
 * medianaga nisbatan»).
 *
 * ⚠️ **Bugungi nuqta BAZAGA kirmaydi** (`p.date < bugun`). Ertalab soat
 * 9 da bugungi foiz tabiiy past; u SURAT bo'lib qoladi, lekin bazani
 * pasaytirsa o'zi o'zini pasaytirardi.
 *
 * ⚠️ `null` daraja (`plannedShifts = 0`) bazadan chiqariladi.
 */
export function davomatDelta(points: TrendPoint[], bugun: string): number | null {
	const bugungi = points.find((nuqta) => nuqta.date === bugun);
	const bugungiFoiz = bugungi ? davomatDarajasi(bugungi.boshlangan, bugungi.plannedShifts) : null;

	if (bugungiFoiz === null) {
		return null;
	}

	const baza = points
		.filter((nuqta) => nuqta.date < bugun)
		.map((nuqta) => davomatDarajasi(nuqta.boshlangan, nuqta.plannedShifts))
		.filter((foiz): foiz is number => foiz !== null);

	if (baza.length === 0) {
		return null;
	}

	return bugungiFoiz - mediana(baza);
}

const SONIYA = 1_000;
const DAQIQA = 60 * SONIYA;
const SOAT = 60 * DAQIQA;

/**
 * «N soniya oldin yangilandi» matni.
 *
 * ⚠️⚠️ **`DASHBOARD_POLL_MS` DAN HOSIL QILINMAYDI** — vaqt oxirgi
 * MUVAFFAQIYATLI javobdan (`dataUpdatedAt`) o'lchanadi. Poll
 * konstantasidan hisoblansa, poll yiqilgan paytda matn abadiy «0–20 s»
 * oralig'ida qolib, ma'lumot eskirganini YASHIRARDI — aynan shu holatni
 * sahifadagi ikkita `Alert` ogohlantiradi.
 *
 * ⚠️ Sof funksiya: hook emas, `now` PARAMETR sifatida keladi. 1 s
 * `setInterval` ni happy-dom'da test qilish beqaror (Bun'ning soxta
 * taymer qo'llovi cheklangan), bu funksiya esa oddiy sonlar bilan test
 * qilinadi.
 *
 * ⚠️ P7 i18n: `t` PARAMETR — `modules/audit/utils/actions.ts`ning
 * `amalNomi(action, t)` bilan bir xil naqsh (chaqiruvchi
 * `useTranslation("dashboard")`dan olingan `t`ni uzatadi).
 */
export function formatYangilangan(dataUpdatedAt: number, now: number, t: TFunction): string {
	if (dataUpdatedAt <= 0) {
		return t("refreshedAgo.never");
	}

	const otgan = Math.max(0, now - dataUpdatedAt);

	if (otgan < 5 * SONIYA) {
		return t("refreshedAgo.justNow");
	}

	if (otgan < DAQIQA) {
		return t("refreshedAgo.seconds", { value: Math.floor(otgan / SONIYA) });
	}

	if (otgan < SOAT) {
		return t("refreshedAgo.minutes", { value: Math.floor(otgan / DAQIQA) });
	}

	return t("refreshedAgo.hours", { value: Math.floor(otgan / SOAT) });
}
